import type { Category, Site } from '../lib/api.ts';

interface Props {
  readonly sites: readonly Site[];
  readonly categories: readonly Category[];
  readonly siteIds: readonly string[];
  readonly categoryIds: readonly string[];
  readonly onSites: (ids: string[]) => void;
  readonly onCategories: (ids: string[]) => void;
}

const toggle = (list: readonly string[], id: string): string[] =>
  list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

/**
 * Sites AND categories, both required.
 *
 * Picking sites but no categories creates a supervisor who can see
 * nothing -- the permission guard denies by default rather than
 * treating "empty" as "everything". Correct, but it looks like a bug to
 * whoever set it up, so the gap is called out here rather than
 * discovered a week later when they say the app is broken.
 */
export const ScopePicker = ({
  sites,
  categories,
  siteIds,
  categoryIds,
  onSites,
  onCategories,
}: Props) => {
  const blind = siteIds.length === 0 || categoryIds.length === 0;

  return (
    <>
      <p className="mono">Sites &mdash; {siteIds.length} of {sites.length}</p>
      <div className="siteChips">
        {sites.map((s) => (
          <button
            key={s.id}
            className={siteIds.includes(s.id) ? 'chip on' : 'chip'}
            onClick={() => onSites(toggle(siteIds, s.id))}
          >
            {s.name}
          </button>
        ))}
      </div>

      <p className="mono" style={{ marginTop: 14 }}>
        Categories &mdash; {categoryIds.length} of {categories.length}
      </p>
      <div className="siteChips">
        {categories.map((c) => (
          <button
            key={c.id}
            className={categoryIds.includes(c.id) ? 'chip on' : 'chip'}
            onClick={() => onCategories(toggle(categoryIds, c.id))}
          >
            {c.name}
          </button>
        ))}
      </div>

      {blind && (
        <p className="scopeWarn">
          Both are required. With one side empty this supervisor sees
          nothing at all.
        </p>
      )}
    </>
  );
};
